import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogFooterProps,
  DialogHeader
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useWindowSize } from "@gettactic/helpers/src/hooks/useWindowSize";
import React from "react";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { MobileDrawer } from "@v2/mobile/MobileDrawer/MobileDrawer";

export interface ModalProps extends React.PropsWithChildren {
  opened: boolean;
  onClose: () => void;
  onOpenChange?: (isOpen: boolean) => void;
  title?: string | JSX.Element;
  size?: string;
  closeOnOverlayClick?: boolean;
  isModal?: boolean;
  className?: string;
  contentClassName?: string;
}

export interface ModalComponent extends React.FC<ModalProps> {
  Body: React.FC<React.PropsWithChildren>;
  Footer: React.FC<DialogFooterProps>;
}

export const Modal: ModalComponent = ({
  children,
  opened,
  onClose,
  title,
  size,
  closeOnOverlayClick = true,
  isModal,
  className,
  contentClassName
}) => {
  const browserWindowSize = useWindowSize();
  const browserHeight = browserWindowSize.height;

  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");

  const titleContent =
    typeof title === "string" ? (
      <span className="font-bold text-2xl text-secondary [.tactic-v2_&]:text-secondary-foreground">
        {title}
      </span>
    ) : (
      title || null
    );

  const content = (
    <div
      style={{
        maxHeight: browserHeight ? `${browserHeight - 200}px` : "32rem"
      }}
      className={cn(
        "pr-3 pl-1",
        browserHeight && browserHeight < 700 ? "overflow-y-auto" : ""
      )}
    >
      {children}
    </div>
  );

  if (!isMd) {
    return (
      <MobileDrawer
        isOpen={opened}
        onOpenChange={(value) => {
          if (!value) onClose();
        }}
      >
        <div className={"flex flex-col gap-4 p-4"}>
          <div>{titleContent}</div>
          <div>{content}</div>
        </div>
      </MobileDrawer>
    );
  }

  return (
    <Dialog open={opened} onOpenChange={onClose} modal={isModal}>
      <DialogContent
        className={cn(`${size ? size : "max-w-[30rem]"}`, className)}
        onOpenAutoFocus={(e) => e.preventDefault()}
        overlayProps={{
          onClick: closeOnOverlayClick ? onClose : undefined
        }}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>{titleContent}</DialogHeader>
        <div
          className={cn(
            `py-4 ${size ? size : "max-w-[30rem]"}`,
            contentClassName
          )}
        >
          {content}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ModalBody: ModalComponent["Body"] = ({ children }) => {
  return <>{children}</>;
};

export const ModalFooter: ModalComponent["Footer"] = (props) => {
  return (
    <DialogFooter {...props} className={cn("mt-4 mb-1", props.className)} />
  );
};

Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
export default Modal;
